import { Card, Col, Empty, Row, Typography } from "antd";
import React, { useLayoutEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "../../../../helpers/pdfWorker";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import LoadingIndicator from "../../../Loaders/LoadingIndicator";


function parsePx(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const n = parseInt(String(value).replace(/px/gi, "").trim(), 10);
  return Number.isFinite(n) ? n : fallback;
}

const isImageFile = (item) =>
  item?.type?.includes("image") ||
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(item?.path || "") ||
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(item?.file_name || "");

function GridPdfCard({
  item,
  index,
  pageNumber,
  type,
  cardWidth,
  cardHeight,
  loadErrors,
  setLoadErrors,
  fill = false,
}) {
  const wrapRef = useRef(null);
  const [pageRenderWidth, setPageRenderWidth] = useState(() => {
    const cap = typeof window !== "undefined" ? window.innerWidth - 48 : 250;
    if (fill) return Math.min(208, cap);
    return Math.max(80, Math.min(parsePx(cardWidth, 250), cap));
  });

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const apply = (w) => {
      if (w > 0) setPageRenderWidth(Math.max(80, Math.floor(w - 8)));
    };
    apply(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      apply(w ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rowHeight = fill ? "100%" : type ? "120px" : "200px";
  const resolvedCardWidth = fill ? "100%" : cardWidth;
  const resolvedCardHeight = fill ? "100%" : cardHeight;

  return (
    <Card
      style={{
        cursor: "pointer",
        width: resolvedCardWidth,
        maxWidth: "100%",
        height: resolvedCardHeight,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
      bodyStyle={{ padding: 0, overflow: "hidden", maxWidth: "100%", height: "100%" }}
    >
      <Row
        style={{
          overflow: "hidden",
          height: rowHeight,
          width: "100%",
          maxWidth: "100%",
          alignItems: "center",
          justifyContent: "center",
          padding: fill ? 0 : "2px 2px 0",
          backgroundColor: "#f3f5f8",
          boxSizing: "border-box",
        }}
      >
        <Col
          span={24}
          style={{
            maxWidth: "100%",
            height: "100%",
            overflow: "hidden",
            display: "flex",
            justifyContent: "center",
            alignItems: fill ? "center" : "flex-start",
          }}
        >
          <div
            ref={wrapRef}
            style={{
              width: "100%",
              maxWidth: "100%",
              height: "100%",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: fill ? "center" : "flex-start",
              boxSizing: "border-box",
            }}
          >
            {isImageFile(item) ? (
              <img
                className="gallery-tile-img"
                alt={item?.file_name || item?.path?.split("/").pop() || "Image"}
                src={item?.url || item?.pdf}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: fill ? "cover" : "contain",
                  objectPosition: "center",
                  display: "block",
                }}
              />
            ) : (
            <Document
              file={item?.url || item?.pdf}
              loading={
                <LoadingIndicator message={"Loading your document, please wait..."} />
              }
              onLoadError={(error) => {
                console.error("PDF load error:", error);
                setLoadErrors((prev) => {
                  const next = [...prev];
                  next[index] = true;
                  return next;
                });
              }}
              error={
                loadErrors[index] ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Empty description="Cannot load pdf file" />
                  </div>
                ) : (
                  "Failed to load PDF"
                )
              }
            >
              <Page
                noData="Unable to read file"
                pageNumber={pageNumber || 1}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                width={pageRenderWidth}
              />
            </Document>
            )}
          </div>
        </Col>
      </Row>

      {type && (
        <Typography.Text
          className="meta-title"
          style={{
            padding: "15px",
            backgroundColor: "#f3f5f8",
            fontSize: "13px",
          }}
        >
          {item?.path}
        </Typography.Text>
      )}
    </Card>
  );
}

function GridPdfViewer({
  data,
  width = null,
  height = null,
  pageNumber,
  type,
  fill = false,
}) {
  const [loadErrors, setLoadErrors] = useState([]);
  const cardWidth = fill ? "100%" : width ?? "250px";
  const cardHeight = fill ? "100%" : height ?? "250px";

  return (
    <>
      {data?.map((item, index) => (
        <GridPdfCard
          key={index}
          item={item}
          index={index}
          pageNumber={pageNumber}
          type={type}
          cardWidth={cardWidth}
          cardHeight={cardHeight}
          loadErrors={loadErrors}
          setLoadErrors={setLoadErrors}
          fill={fill}
        />
      ))}
    </>
  );
}

export default GridPdfViewer;
